import {useRef, useState} from 'react';
import type {AuthState} from '../contexts/AuthContext.js';
import type {PigFeederGroup} from '../lib/pig.js';
import {newPigRequestId, pigRejectionMessage, type PigOpOutcome} from '../lib/pigOperationsApi.js';
import type {FeederOpRunner} from './pigFeederOps.js';

// Mortality goes through pig_apply_operation (migration 231): the server stamps
// the author, the farm date and created_at, writes the record under the feeder
// document's lock, and logs the pig.batch Activity in the SAME transaction. The
// client therefore sends only what the operator typed and emits no Activity.
// The entry id and the request id survive a retry; the modal closes only on a
// confirmed outcome.

export type PigNotice = {kind: string; message: string};

type MortalityModal = {batchId: string};
type MortalityForm = {sub_batch_id: string; count: string; comment: string};
type PendingAppend = {entryId: string; requestId: string; intent: string};

export type UsePigMortalityDeps = {
  feederGroups: PigFeederGroup[];
  applyFeederOp: FeederOpRunner;
  setNotice: (notice: PigNotice | null) => void;
  // Kept for the hook's signature; the server identifies the author.
  authState?: AuthState;
};

function outcomeNotice(outcome: PigOpOutcome, failed: string): string | null {
  if (outcome.status === 'rejected') return failed + pigRejectionMessage(outcome);
  if (outcome.status === 'unknown') return failed + outcome.message;
  return null;
}

export function usePigMortality({feederGroups, applyFeederOp, setNotice}: UsePigMortalityDeps) {
  const [mortalityModal, setMortalityModal] = useState<MortalityModal | null>(null);
  const [mortalityForm, setMortalityForm] = useState<MortalityForm>({sub_batch_id: '', count: '', comment: ''});
  const [mortalityBusy, setMortalityBusy] = useState(false);
  const [expandedMortality, setExpandedMortality] = useState<string | null>(null);
  const busy = useRef(false);
  const pendingAppend = useRef<PendingAppend | null>(null);

  function openMortalityModal(batchId: string) {
    if (busy.current) return;
    pendingAppend.current = null;
    setNotice(null);
    setMortalityModal({batchId});
    setMortalityForm({sub_batch_id: '', count: '', comment: ''});
  }

  async function saveMortality() {
    if (!mortalityModal || busy.current) return;
    setNotice(null);
    const count = parseInt(mortalityForm.count);
    if (!Number.isFinite(count) || count <= 0) {
      setNotice({kind: 'error', message: 'Enter a count of 1 or more.'});
      return;
    }
    const batchId = mortalityModal.batchId;
    if (!feederGroups.some((g) => g.id === batchId)) {
      setNotice({kind: 'error', message: 'Save failed: this batch is no longer available.'});
      return;
    }
    const change: Record<string, unknown> = {count};
    if (mortalityForm.sub_batch_id) change.sub_batch_id = mortalityForm.sub_batch_id;
    const comment = (mortalityForm.comment || '').trim();
    if (comment) change.comment = comment;
    // One entry identity per open modal; one request id per distinct attempt.
    // A retry of the SAME answers reuses the request id (the server replays);
    // changed answers take a new one under the same entry id, so a first save
    // that did land is reported as "already saved" instead of doubling up.
    const intent = JSON.stringify(change);
    const prior = pendingAppend.current;
    const entryId = prior?.entryId || newPigRequestId().replace(/^pig-/, 'pm-');
    const requestId = prior && prior.intent === intent ? prior.requestId : newPigRequestId();
    pendingAppend.current = {entryId, requestId, intent};
    setMortalityBusy(true);
    busy.current = true;
    const outcome = await applyFeederOp({
      requestId,
      operation: 'mortality.append',
      target: {feeder_id: batchId, mortality_id: entryId},
      change,
    });
    setMortalityBusy(false);
    busy.current = false;
    const saved =
      outcome.status === 'saved' ||
      outcome.status === 'no_change' ||
      (outcome.status === 'rejected' && outcome.code === '23505');
    if (!saved) {
      setNotice({kind: 'error', message: outcomeNotice(outcome, 'Save failed: ') || 'Save failed.'});
      return;
    }
    if (outcome.status === 'rejected') {
      setNotice({kind: 'success', message: 'This entry had already been saved; the list shows the saved values.'});
    }
    pendingAppend.current = null;
    setMortalityModal(null);
  }

  async function deleteMortality(batchId: string, entryId: string) {
    if (!window._wcfConfirmDelete) return;
    window._wcfConfirmDelete('Delete this mortality entry?', async () => {
      if (busy.current) return;
      busy.current = true;
      setNotice(null);
      try {
        const outcome = await applyFeederOp({
          requestId: newPigRequestId(),
          operation: 'mortality.delete',
          target: {feeder_id: batchId, mortality_id: entryId},
        });
        const failed = outcomeNotice(outcome, 'Delete failed: ');
        if (failed) setNotice({kind: 'error', message: failed});
      } finally {
        busy.current = false;
      }
    });
  }

  return {
    mortalityModal,
    setMortalityModal: (value: MortalityModal | null) => {
      if (!busy.current) setMortalityModal(value);
    },
    mortalityForm,
    setMortalityForm: (value: MortalityForm) => {
      if (!busy.current) setMortalityForm(value);
    },
    mortalityBusy,
    expandedMortality,
    setExpandedMortality,
    openMortalityModal,
    saveMortality,
    deleteMortality,
  };
}
